<template>
  <q-list bordered class="q-pa-md q-pa-lg-lg bg-white more-rounded-borders" v-if="plan">
    <q-expansion-item v-model="expanded" header-class="text-bold rounded-borders-10" header-style="font-size: 20px"
                      icon="o_shopping_cart" label="Your Plan">

      <q-card>

        <q-card-actions class="q-my-md">

          <div class="flex justify-start items-center">
            <q-img width="80px" class="lt-sm q-mr-sm"
                   :src="image_url" @error="set_fallback_url"></q-img>
            <q-img width="100px" class="gt-xs q-mr-md"
                   :src="image_url" @error="set_fallback_url"></q-img>
            <div class="text-bold text-125">{{ plan_name(plan) }}</div>
          </div>

          <q-space></q-space>

          <q-btn v-if="show_edit" flat label="Change Plan" class="text-bold bg-blue-1 text-primary" no-caps
                 @click="change_plan"></q-btn>

        </q-card-actions>

        <q-separator></q-separator>

        <q-card-section>

          <div class="text-left q-mb-md">
            <div class="text-bold text-100 block" v-if="!show_historical_usage">
              <div>Average Price Per KWh:</div>
              <div class="text-bold text-primary text-200 cursor-pointer" @click="show_details = true">
                <plan-average-price :plan="plan"></plan-average-price>
              </div>
              <div>
                Based on <span @click="show_details = true" class="cursor-pointer"
                               style="border-bottom: 2px dotted #607d8b"> {{ usage }} KWh</span> usage
              </div>
            </div>
            <div class="text-bold text-100 block" v-else>
              <div>Average Price Per KWh:</div>
              <div class="text-bold text-primary text-200">
                <plan-average-price :plan="plan"></plan-average-price>
              </div>
              <div>
                Based on your
                <span @click="show_details = true" class="cursor-pointer" style="border-bottom: 2px dotted #607d8b">
                  {{ historical_usage_count }} months
                </span> of usage history
              </div>
            </div>
          </div>


          <div class="flex column q-gutter-y-xs">
            <div class="flex q-gutter-x-sm text-100">
              <div class="text-blue-grey-6"> Provider Name:</div>
              <div class="text-bold">{{ plan_brand(plan) }} (PUCT# {{ plan.product.brand.puct_number }})</div>
            </div>

            <div class="flex q-gutter-x-sm text-100">
              <div class="text-blue-grey-6"> Utility:</div>
              <div class="text-bold">{{ plan.tdsp.short_name }}</div>
            </div>

            <div class="flex q-gutter-x-sm text-100">
              <div class="text-blue-grey-6"> Agreement Term:</div>
              <div class="text-bold">
                {{ plan.product.term_string }}
              </div>
            </div>

            <!--          <div class="flex q-gutter-x-sm text-100">-->
            <!--            <div class="text-blue-grey-6"> Rate Type:</div>-->
            <!--            <div class="text-bold">{{ plan.product.term > 1 ? 'Fixed' : 'Variable' }}</div>-->
            <!--          </div>-->

            <div class="flex q-gutter-x-sm text-100">
              <div class="text-blue-grey-6"> Early Termination Fee:</div>
              <div class="text-bold">
                {{ plan.product.early_termination_fee_text }}
              </div>
              <div class="text-italic text-0875 text-blue-grey-6">
                (This fee <span class="text-bold">will not be charged</span> if you end your contract early because you
                are moving out)
              </div>
            </div>
          </div>

        </q-card-section>

        <q-card-section horizontal class="justify-start item-start flex q-my-md">
          <q-btn flat icon="description" no-caps label="Facts Label" color="primary" class="text-100"
                 :href="plan.document_links.find(x=> x.type === 'efl').link" target="_blank"></q-btn>
          <q-btn flat icon="description" no-caps label="Terms of Service" color="primary" class="text-100"
                 :href="plan.document_links.find(x=> x.type === 'tos').link" target="_blank"></q-btn>
          <q-btn flat icon="description" no-caps label="YRAC" color="primary" class="text-100"
                 :href="plan.document_links.find(x=> x.type === 'yraac').link" target="_blank"></q-btn>
        </q-card-section>

      </q-card>

    </q-expansion-item>
  </q-list>

  <q-dialog v-model="show_details" class="gt-md" persistent no-shake>
    <q-card style="min-width: 1200px">
      <q-card-section class="row items-center">
        <div class="text-150">Plan Details</div>
        <q-space/>
        <q-btn icon="cancel" size="lg" flat round dense v-close-popup/>
      </q-card-section>
      <pricing-details :plan="plan"></pricing-details>
    </q-card>
  </q-dialog>
  <q-dialog v-model="show_details" class="lt-lg" full-width persistent no-shake>
    <q-card>
      <q-card-section class="row items-center">
        <div class="text-150">Plan Details</div>
        <q-space/>
        <q-btn icon="cancel" size="lg" flat round dense v-close-popup/>
      </q-card-section>
      <pricing-details :plan="plan"></pricing-details>
    </q-card>
  </q-dialog>
</template>

<script>
import { computed, onMounted, ref, toRef, watch } from "vue";
import Utilities from "src/composables/Utilities";
import Analytics from "src/composables/Analytics";
import { useRouter } from "vue-router";
import UsageFilters from "src/composables/UsageFilters";
import PlanDetails from "components/plans/cards/PlanDetails";
import PricingDetails from "components/plans/cards/PricingDetails";
import Plans from "src/composables/Plans";
import PlanAveragePrice from "components/plans/misc/PlanAveragePrice.vue";
import PlanAverageDescription from "components/plans/misc/PlanAverageDescription.vue";

export default {
  name: "SelectedPlanCard",
  components: { PlanAverageDescription, PlanAveragePrice, PricingDetails, PlanDetails },
  props: {
    plan: {
      type: Object,
      required: true
    },
    usage: {
      type: Number,
      default: 500
    },
    show_edit: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const plan = ref(null);
    const plan_ro = toRef(props, 'plan');
    const { show_historical_usage, historical_usage_values, historical_usage_count, usage_selected } = UsageFilters();
    const { calculate_plan_for_usage, plan_map, historical_usage_plan_map } = Plans();
    const { plan_brand, plan_name, plan_brand_image_name } = Utilities();
    const expanded = ref(true);
    const { log_event } = Analytics();
    const router = useRouter();
    const change_plan = () => {
      log_event({ name: 'co_btn_change_plan' })
      router.push('/');
    }
    const show_details = ref(false);

    const svg_image_url = computed(() => {
      return `${ process.env.ASSETS_URL }images/${ plan_brand_image_name(props.plan) }.svg`
    })
    const alt_image_url = computed(() => {
      return `${ process.env.ASSETS_URL }images/${ plan_brand_image_name(props.plan) }.png`
    })

    const use_fallback = ref(false);
    const set_fallback_url = function () {
      use_fallback.value = true
    }

    const image_url = computed(() => {
      if (use_fallback.value) {
        return alt_image_url.value;
      }
      else {
        return svg_image_url.value || alt_image_url.value
      }
    });


    const format_plan = async () => {
      plan.value = plan_ro.value;
      if (plan.value) {
        const missing_usages = [];
        if (show_historical_usage.value) {
          for (const hi of historical_usage_values.value) {
            if (!plan.value[`display_pricing_${ hi }`]) {
              missing_usages.push(hi)
            }
          }
        }
        else if (!plan.value[`display_pricing_${ usage_selected.value }`]) {
          missing_usages.push(usage_selected.value)
        }
        for (const usage of missing_usages) {
          plan.value[`display_pricing_${ usage }`] = await calculate_plan_for_usage({
            plan_id: plan.value._id,
            usage: usage
          })
        }
        if (show_historical_usage.value) {
          plan.value = historical_usage_plan_map(plan.value)
        }
        else {
          plan.value = plan_map(plan.value)
        }
      }
    }

    watch([plan_ro], () => {
      format_plan()
    })

    onMounted(() => {
      format_plan()
    })

    return {
      show_details,
      expanded,
      plan_brand,
      plan_name,
      change_plan,
      show_historical_usage,
      historical_usage_count,
      image_url,
      set_fallback_url
    }
  }
}
</script>

<style scoped>

</style>
